import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PropertyTypeFilters from './property_filters/property_type_filters';
import BuildingFilters from './property_filters/building_filters';
import HospitalityFilters from './property_filters/hospitality_filters';
import IndustrialFilters from './property_filters/industrial_filters';
import MultifamilyFilters from './property_filters/multifamily_filters';

import { filterLabelMappings, filterKeyMappings } from 'bundles/research/index/research_constants';

const industrialFiltersList  = [
  "lotStabilization",
  "servedRailProvider",
  "lotBorderType",
  "availableRailProvider"
];

export default class PropertyFilterGroup extends React.Component {
  static propTypes = {
    assetClasses: PropTypes.array.isRequired,
    availableRailProviders: PropTypes.array.isRequired,
    buildingClasses: PropTypes.array.isRequired,
    clearFiltersIfEmpty: PropTypes.func.isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    industrialFilter: PropTypes.object.isRequired,
    investmentTypes: PropTypes.array.isRequired,
    lotBorderTypes: PropTypes.array.isRequired,
    lotStabilizations: PropTypes.array.isRequired,
    multifamilyFilter: PropTypes.object.isRequired,
    propertyFilter: PropTypes.shape({
      lotUnits: PropTypes.string,
      apnNumber: PropTypes.string,
      maxBuildingSize: PropTypes.string,
      maxCeilingHeight: PropTypes.string,
      maxLotSize: PropTypes.string,
      maxMortgageAmount: PropTypes.string,
      maxMortgageRecordedDate: PropTypes.string,
      maxNumUnits: PropTypes.string,
      maxYearBuilt: PropTypes.string,
      minBuildingSize: PropTypes.string,
      minCeilingHeight: PropTypes.string,
      minLotSize: PropTypes.string,
      minMortgageAmount: PropTypes.string,
      minMortgageRecordedDate: PropTypes.string,
      minNumUnits: PropTypes.string,
      minYearBuilt: PropTypes.string
    }).isRequired,
    selectedAssetClasses: PropTypes.array.isRequired,
    selectedBuildingClasses: PropTypes.array.isRequired,
    selectedInvestmentTypes: PropTypes.array.isRequired,
    servedRailProviders: PropTypes.array.isRequired,

    onAssetClassChange: PropTypes.func.isRequired,
    onBuildingClassChange: PropTypes.func.isRequired,
    onIndustrialFilterChange: PropTypes.func.isRequired,
    onInvestmentTypeChange: PropTypes.func.isRequired,
    onMultifamilyFilterChange: PropTypes.func.isRequired,
    onPropertyFilterChange: PropTypes.func.isRequired
  };

  state = {
    activeFilter: "general"
  }

  handleRetrieveRecords = () => {
    const { clearFiltersIfEmpty, delayRetrieveRecords } = this.props;
    clearFiltersIfEmpty();
    delayRetrieveRecords();
  };

  handlePropertyFilterChange = (e, name) => {
    const { propertyFilter, onPropertyFilterChange } = this.props;

    let value = "";
    if (e && e.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    propertyFilter[name] = value || null;
    onPropertyFilterChange(propertyFilter);
    this.handleRetrieveRecords();
  };

  handleIndustrialFilterChange = (e, name, checkbox = false) => {
    let value = e ? e.target.value : "";
    if (checkbox) {
      value = e.target.checked;
    }

    const { industrialFilter, onIndustrialFilterChange } = this.props;

    if (industrialFiltersList.includes(name)) {
      industrialFilter[name] = industrialFilter[name] === value ? "" : value;
    } else {
      industrialFilter[name] = value;
    }
    onIndustrialFilterChange(industrialFilter);
    this.handleRetrieveRecords();
  };

  handleMultifamilyFilterChange = (e, name) => {
    const { multifamilyFilter, onMultifamilyFilterChange } = this.props;

    let value = '';
    if (e && e.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    multifamilyFilter[name] = value || null;
    onMultifamilyFilterChange(multifamilyFilter);
    this.handleRetrieveRecords();
  };

  handleAssetClassChange = (e) => {
    const { onAssetClassChange } = this.props;
    onAssetClassChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  handleBuildingClassChange = (e) => {
    const { onBuildingClassChange } = this.props;
    onBuildingClassChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  handleInvestmentTypeChange = (e) => {
    const { onInvestmentTypeChange } = this.props;
    onInvestmentTypeChange(e.target.value, e.target.checked);
    this.handleRetrieveRecords();
  };

  render() {
    const { assetClasses, selectedAssetClasses } = this.props;

    return (
      <React.Fragment>
        <PropertyTypeFilters
          assetClasses={assetClasses}
          selectedAssetClasses={selectedAssetClasses}
          onAssetClassChange={this.handleAssetClassChange}
        />
        {this.renderPropertyFilters()}
      </React.Fragment>
    );
  }

  renderPropertyFilters() {
    const { activeFilter } = this.state;

    const activePillClass = classNames({
      "pull-left": true,
      "clickable": true,
      "badge": true,
      "badge-pill-active": true,
      "right-buffer-sm": true
    });

    const mutedPillClass = classNames({
      "pull-left": true,
      "clickable": true,
      "badge": true,
      "badge-pill": true,
      "right-buffer-sm": true
    });

    return (
      <React.Fragment>
        <h4 className="hidden-xs hidden-sm bold mb-0 mt-5">Building Information</h4>
        <hr className="hidden-xs hidden-sm mt-2 mb-2"/>
        <div className="filters-block hidden-xs hidden-sm">
          <div
            className={activeFilter === "general" ? activePillClass : mutedPillClass}
            onClick ={() => this.setState({ activeFilter: "general" })}
          >
            General
          </div>
          <div
            className={activeFilter === 'industrial' ? activePillClass : mutedPillClass}
            onClick ={() => this.setState({ activeFilter: 'industrial' })}
          >
            Industrial
          </div>
          <div
            className={activeFilter === 'multifamily' ? activePillClass : mutedPillClass}
            onClick ={() => this.setState({ activeFilter: 'multifamily' })}
          >
            Multifamily
          </div>
          <div
            className={activeFilter === 'hospitality' ? activePillClass : mutedPillClass}
            onClick ={() => this.setState({ activeFilter: 'hospitality' })}
          >
            Hospitality
          </div>
        </div>
        <div className="property-group-container">
          <div className="building-filters">
            {this.renderFilters()}
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderFilters() {
    const { activeFilter } = this.state;

    switch (activeFilter) {
    case "general":
      return (
        <React.Fragment>
          {this.renderBuildingFilters()}
        </React.Fragment>
      );
    case 'multifamily':
      return (
        <React.Fragment>
          {this.renderMultifamilyFilters()}
        </React.Fragment>
      );
    case "hospitality":
      return (
        <React.Fragment>
          {this.renderHospitalityFilters()}
        </React.Fragment>
      );
    case "industrial":
      return (
        <React.Fragment>
          {this.renderIndustrialFilters()}
        </React.Fragment>
      );
    }
  }

  renderIndustrialFilters() {
    const {
      availableRailProviders,
      lotBorderTypes,
      lotStabilizations,
      industrialFilter,
      servedRailProviders
    } = this.props;


    return (
      <IndustrialFilters
        availableRailProviders={availableRailProviders}
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        industrialFilter={industrialFilter}
        lotBorderTypes={lotBorderTypes}
        lotStabilizations={lotStabilizations}
        servedRailProviders={servedRailProviders}
        onIndustrialFilterChange={this.handleIndustrialFilterChange}
      />
    );
  }

  renderMultifamilyFilters() {
    const { multifamilyFilter } = this.props;

    return (
      <MultifamilyFilters
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        multifamilyFilter={multifamilyFilter}
        onMultifamilyFilterChange={this.handleMultifamilyFilterChange}
      />
    );
  }

  renderHospitalityFilters() {
    const { propertyFilter } = this.props;

    return (
      <HospitalityFilters
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        propertyFilter={propertyFilter}
        onPropertyFilterChange={this.handlePropertyFilterChange}
      />
    );
  }

  renderBuildingFilters() {
    const buildingClassOptions = this.props.buildingClasses.map(building => (
      { id: building, value: building, label: building }
    ));

    const {
      investmentTypes,
      propertyFilter,
      selectedBuildingClasses,
      selectedInvestmentTypes
    } = this.props;

    return (
      <BuildingFilters
        buildingClasses={buildingClassOptions}
        filterKeyMappings={filterKeyMappings}
        filterLabelMappings={filterLabelMappings}
        investmentTypes={investmentTypes}
        propertyFilter={propertyFilter}
        selectedBuildingClasses={selectedBuildingClasses}
        selectedInvestmentTypes={selectedInvestmentTypes}
        onBuildingClassChange={this.handleBuildingClassChange}
        onInvestmentTypeChange={this.handleInvestmentTypeChange}
        onPropertyFilterChange={this.handlePropertyFilterChange}
      />
    );
  }
}
